/* Copyright (c) 2020-present CloudIO Inc */

import { BoxProps, isPropertyVisible } from '../Box/BoxExports';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './BoxContainer';
import { getCustomChildItemTypes } from 'componentregistry/registry/Manager';
import { helpRenders } from '../../help/sample_pages/BoxContainerPage';

const name = 'Box Container';

const BoxContainerManager: ComponentManagerFn<'BoxContainer'> = (
  platform: Platform,
): ComponentManager<'BoxContainer'> => {
  return {
    ...baseComponentManager<'BoxContainer'>(platform, 'BoxContainer'),
    defaultValue,
    defaultChildren: [],
    isChildAllowed: (page, item, childType) =>
      (
        [
          ...getCustomChildItemTypes('BoxContainer'),
          'Accordion',
          'Alert',
          'AutoSizer',
          'Box',
          'BoxContainer',
          'BoxList',
          'BreadcrumbNavigation',
          'Button',
          'ButtonDropdown',
          'ButtonGroup',
          'Card',
          'Chart',
          'Chip',
          'DataStoreReference',
          'Divider',
          'Form',
          'Icon',
          'IconButton',
          'IconSwitch',
          'Markdown',
          'Mirror',
          'RichTextEditor',
          'SearchForm',
          'SmartSearch',
          'TabPanel',
          'Toolbar',
        ] as ItemType[]
      ).includes(childType),
    isPropertyVisible: (property, itemProps) => {
      switch (property) {
        case 'component':
        case 'fontSize':
        case 'fontStyle':
        case 'fontWeight':
        case 'color':
        case 'margin':
          return false;
        default:
          return isPropertyVisible(property, itemProps);
      }
    },
    props: BoxProps,
    category: 'Containers',
    name,
    canHaveChildren: () => true,
    layout: 'auto',
    icon: { iconName: 'box' },
    description: `**Overview**<br/>
The Box Container is a fundamental structural component used to group, align, and organize other UI elements. Unlike visual components (like Cards or Buttons), the Box Container does not add its own visual styles such as borders or shadows by default. Instead, it acts as an invisible wrapper, providing the essential scaffolding needed to build complex, well-aligned layouts without adding visual clutter.
To maintain a strict separation of concerns, this component focuses exclusively on layout. Properties such as Font Size, Text Color, and Font Weight are intentionally disabled. If you need to style text, apply those settings directly to the child components (like Text or Markdown nodes) rather than the container.

**1. Layout & Spacing Control**<br/>
The primary function of the Box Container is to define the physical space its children occupy. Use these properties to manage the "breathing room" and dimensions of your layout.
* **Width & Height:** Constraints the specific size of the component (e.g., 100%, 500px, 50vh). You can define these values to ensure the container fits a specific area of the dashboard or adapts responsively to the screen size.
* **Min Width / Min Height:** Ensures the component never becomes smaller than these values, preventing content from collapsing or disappearing on small screens.
* **Max Width / Max Height:** Ensures the component never grows larger than these values, which is useful for keeping readable line lengths on ultra-wide monitors.
* **Padding:** Defines the internal spacing between the edges of the container and the content inside it. Proper padding ensures that nested components do not touch the borders of the layout.
* **Margin:** Adds invisible spacing outside the container, pushing it away from neighboring components.
* **Overflow:** Decides what happens if the content inside becomes larger than the container itself. (Options: Unset/Visible, Hidden, Scroll).

**2. Content Alignment & Orientation (Flexbox)**<br/>
Once components are grouped inside the Box Container, their arrangement is controlled by powerful Flexbox layout properties.
* **Display:** Defines the layout mode. The default Flex mode enables powerful alignment controls, whereas other modes treat the container as a standard block.
* **Flex Direction:** Determines the main axis of the layout. (Row for horizontal, Column for vertical).
* **Justify Content:** Controls alignment along the main axis (horizontal for Row, vertical for Column). Use this to distribute space (e.g., Center, Space Between).
* **Align Items:** Controls alignment along the cross axis. For example, in a horizontal row, this determines if items are centered vertically or stretched to fill the height.
* **Gap:** Sets the physical distance (gutter) between child items, removing the need to add manual margins to every child component.
* **Flex Wrap:** Controls behavior when items run out of space. (Nowrap forces single line, Wrap allows flow to new line).

**3. Visual Styling**<br/>
While the Box Container is primarily for structure, it supports essential styling properties to create backgrounds, panels, and distinct sections.
* **Background Color:** Sets the solid fill color of the container.
* **Hover Background Color:** Sets a different color that appears only when the user hovers over the container, providing visual feedback.
* **Background Image:** Allows you to set a URL for a background image.
* **Border:** Defines the thickness, style, and color of the boundary line around the component.
* **Border Radius:** Rounds the corners of the container. Use 50% to create a perfect circle if the box is square.
* **Box Shadow:** Adds a drop shadow effect to create depth and elevation.

**4. Structural Composition**<br/>
The Box Container is designed to be highly nestable, allowing you to build intricate interface hierarchies.
* **Grouping:** By placing multiple components (such as Charts, Buttons, or Text) inside a single Box Container, you treat them as a single unit. This is useful for moving, hiding, or duplicating entire sections of a UI at once.
* **Nesting:** You can place Box Containers inside other Box Containers to create grid-like structures or complex dashboards with distinct sections (e.g., a "Sidebar" container next to a "Main Content" container).

**5. Interaction & Feedback**<br/>
These properties enhance the user experience by adding interactivity and helpful context to the container.
* **On Hover Animation:** Enables predefined visual effects (like scaling up or lifting) when the user interacts with the component.
* **Show Only On Hover:** A visibility toggle. If configured, specific child elements will remain hidden until the user hovers over this container.
* **Tooltip Text:** A short message displayed in a small pop-up box when the user hovers over the component.
* **Tooltip Position:** Determines where the tooltip appears relative to the component (e.g., Top, Bottom).`,
    helpRenders,
  };
};

export default BoxContainerManager;
